import { Box } from '@mui/material';
import { useTablesInput } from 'providers/TablesInputProvider';
import { lazy, Suspense, useState } from 'react';
import { EmptyTableState } from 'components/EmptyTableState';
import { CartTotalsSection, QuoteList } from 'generated/graphql';
import { QuoteCardSkeleton } from './QuotesSkeletons';

const QuoteCard = lazy(() => import('./QuoteCard/QuoteCard'));

/**
 * The section whose cards show quote totals, by its stable identifier.
 *
 * Keyed on the identifier rather than the title because the title is display
 * copy: renaming "To review" would otherwise switch totals off with nothing
 * failing.
 */
const SECTION_WITH_TOTALS = 'quotes-to-review';

/**
 * Renders a table group's quotes as cards.
 *
 * The third member of DataDisplay, alongside expandable tables and charts: a
 * group holds quotes the same way it holds rows, so the tabs, stage control,
 * lazy loading, section title and pagination all come from the shared
 * machinery and only the cards are this screen's own.
 */
const QuoteListView = ({ list, onReload }: { list: QuoteList; onReload: () => void }) => {
  const { activeTabIndex } = useTablesInput();
  // Totals are fetched per card and patched in here rather than refetching the
  // screen. Local because it is per-render state, not something the server or
  // another group needs to know about.
  const [totals, setTotals] = useState<Record<string, CartTotalsSection[]>>({});

  const quotes = list.quotes ?? [];

  if (list.placeholder) {
    return (
      <Box sx={{ my: 2 }}>
        <EmptyTableState placeholder={list.placeholder} iconSize={40} onRefresh={onReload} />
      </Box>
    );
  }

  return quotes.map((quote) => (
    <Box key={quote.quoteID} sx={{ mb: 1 }}>
      <Suspense fallback={<QuoteCardSkeleton />}>
        <QuoteCard
          quote={totals[quote.quoteID] ? { ...quote, quoteTotals: totals[quote.quoteID] } : quote}
          hasQuoteTotals={list.identifier === SECTION_WITH_TOTALS}
          tabIndex={activeTabIndex}
          onReload={onReload}
          setQuoteCartTotals={(id, next) => setTotals((prev) => ({ ...prev, [id]: next }))}
        />
      </Suspense>
    </Box>
  ));
};

export default QuoteListView;
